import {
  AfterEffectWithFilterAndMask,
  BendingTextProperties,
  Configuration,
  ConfigurationBase,
  DesignConfiguration,
  DesignProperties,
  Draft,
  DraftConfiguration,
  DraftSpecialTextConfiguration,
  DraftTextOrPlaceholderTextConfiguration,
  Product,
  ProductTask,
  Size,
  SpecialTextProperties,
  TextConfiguration,
} from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { MM_PER_INCH } from '@/utils/math/consts';
import { textAlignToAnchor } from '@/utils/text/textAlignToAnchor';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import {
  isDraftBendingTextConfiguration,
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { getAppearance } from '@/model/draft/getAppearance';
import { getConfiguration } from '@/model/draft/getConfiguration';
import { getProductType } from '@/model/draft/getProductType';
import { getViewForPreview } from '@/model/draft/getViewForPreview';
import { isDigital } from '@/model/printType/isDigital';

function getProperties(
  configuration: DraftConfiguration,
  task: ProductTask
): DesignProperties | SpecialTextProperties | BendingTextProperties | undefined {
  if (isDraftDesignConfiguration(configuration)) {
    const { filter, mask } = configuration;
    if (filter || mask) {
      const afterEffect = {
        originalDesign: { id: configuration.design.id, href: `/u${configuration.design.id}` },
      } as AfterEffectWithFilterAndMask;

      if (filter) {
        afterEffect.filter = {
          settings: filter.settings,
          designId: assertDefined(task.filterDesignId),
        };
      }

      if (mask) {
        afterEffect.id = mask.id;
        afterEffect.offset = mask.offset;
        afterEffect.scale = mask.scale;
        afterEffect.version = mask.version;
      }

      return {
        type: 'afterEffect',
        afterEffect,
      };
    }
    return;
  }

  if (isDraftSpecialTextConfiguration(configuration)) {
    return {
      alignment: configuration.text.style.textAlign,
      text: configuration.text.content,
      specialText: true,
      fontId: configuration.text.style.font.id,
    };
  }

  if (isDraftBendingTextConfiguration(configuration)) {
    return {
      type: 'bendingText',
      angle: configuration.text.style.angle,
      fill: configuration.printColors[0].fill,
      fontFamilyId: configuration.text.style.font.familyId,
      fontSize: configuration.text.style.fontSize,
      fontStyle: configuration.text.style.font.style,
      fontWeight: configuration.text.style.font.weight,
      scale: configuration.scale,
      text: configuration.text.content,
    };
  }
}

function mapTextConfig(
  config: DraftTextOrPlaceholderTextConfiguration,
  productConfigBase: ConfigurationBase,
  size: Size
): TextConfiguration {
  const isDigitalPrintType = isDigital(config.printType);
  return {
    ...productConfigBase,
    type: 'text',
    content: {
      dpi: String(MM_PER_INCH),
      unit: 'mm',
      svg: {
        text: {
          transform: config.rotation
            ? `rotate(${config.rotation}, ${size.width / 2}, ${size.height / 2})`
            : '',
          fontFamilyId: config.text.style.font.familyId,
          fontId: config.text.style.font.id,
          fill: config.printColors[0].fill,
          fontWeight: config.text.style.font.weight,
          fontStyle: config.text.style.font.style,
          fontSize: `${config.text.style.fontSize}`,
          printColorRGB: isDigitalPrintType ? config.printColors[0].fill : undefined,
          printColorId: isDigitalPrintType ? undefined : config.printColors[0].id,
          content: config.text.content.split('\n').map((content, index) => ({
            content: [content],
            textAnchor: textAlignToAnchor(config.text.style.textAlign),
            x: `${config.text.style.x}`,
            y: `${config.text.style.y + index * config.text.style.fontSize * 1.2}`,
          })),
        },
      },
    },
  };
}

function mapDesign(
  configuration: DraftConfiguration,
  productConfigBase: ConfigurationBase,
  size: Size,
  task: ProductTask
): DesignConfiguration {
  let designId;
  if (task) {
    designId = task.designId;
  } else if (isDraftDesignConfiguration(configuration)) {
    designId = configuration.design.id;
  }

  if (!designId) {
    throw new Error(
      `Got a configuration of type ${configuration.type} but could not determine a designId!`
    );
  }

  const isDigitalPrintType = isDigital(configuration.printType);

  return {
    ...productConfigBase,
    type: 'design',
    specialFontId: isDraftSpecialTextConfiguration(configuration)
      ? (configuration as DraftSpecialTextConfiguration).text.style.font.id
      : null,
    content: {
      dpi: String(MM_PER_INCH),
      unit: 'mm',
      svg: {
        image: {
          transform: configuration.rotation
            ? `rotate(${configuration.rotation}, ${size.width / 2}, ${size.height / 2})`
            : '',
          width: size.width,
          height: size.height,
          designId,
          printColorRGBs: isDigitalPrintType
            ? configuration.printColors.map((pc) => pc.fill).join(' ')
            : undefined,
          printColorIds: isDigitalPrintType
            ? undefined
            : configuration.printColors
                .map((pc) => pc.id)
                .filter(Boolean)
                .join(' '),
        },
      },
    },
    properties: getProperties(configuration, task),
  };
}

function mapDraftConfigToProductConfig(
  config: DraftConfiguration,
  task: ProductTask
): Configuration {
  const productConfigBase: ConfigurationBase = {
    type: config.type,
    offset: config.offset,
    printArea: { id: config.printArea.id },
    printType: { id: config.printType.id },
    restrictions: { changeable: true },
  };
  const size = getCurrentSizeOfConfiguration(config);
  switch (config.type) {
    case 'text':
    case 'placeholderText':
      return mapTextConfig(config, productConfigBase, size);
    case 'design':
    case 'specialText':
    case 'bendingText':
      return mapDesign(config, productConfigBase, size, task);
  }
}

export function mapDraftToProduct(draft: Draft, tasks: ProductTask[]): Product {
  return {
    productType: { id: getProductType(draft).id },
    appearance: { id: getAppearance(draft).id },
    defaultValues: { defaultView: { id: getViewForPreview(draft).id } },
    creator: 's-omats',
    restrictions: {
      example: false,
      freeColorSelection: true,
    },
    configurations: draft.configurationIds.map((cid) => {
      const configuration = getConfiguration(draft, cid);
      return mapDraftConfigToProductConfig(
        configuration,
        tasks.find((t) => t.cid === cid)!
        // tasks only exists for special configs but then they are required
        // not sure how to type this
      );
    }),
  };
}
